import {
  Badge,
  Box,
  Center,
  Collapse,
  Heading,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getSecondLevelEntities, getShipments } from "../service";
import { buildAddress } from "../../../utils/util";
import { FaBox, FaMapMarkerAlt, FaTruck, FaTimes, FaMotorcycle } from "react-icons/fa";
import { GaraazTextComponent } from "@svan-garaaz/reactsharedlib";
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import SecondLevelCard from "./SecondLevelCard";
import {
  FirstLevelEntity,
  SecondLevelEntity,
  Shipment,
  ShipmentFilters,
  ShipmentGrouping,
} from "../types";
import EngagementModeBadge from "../../../components/EngagementModeBadge";
import { FcShipped } from "react-icons/fc";
import { ClientId } from "../../../types";
import { getGlobalApiConfig } from "../../../config/ApiConfig";
import ShipmentsTable from "./ShipmentsTable";

export interface FirstLevelCardProps {
  entity: FirstLevelEntity;
  expanded: boolean;
  onToggleExpansion: () => void;
  isLoading?: boolean;
  grouping: ShipmentGrouping;
  statusFilter: "undelivered" | "delivered" | "cancelled";
  filters?: ShipmentFilters;
  onShipmentsChanged?: () => void | Promise<void>;
}

export default function FirstLevelCard({
  entity,
  expanded,
  onToggleExpansion,
  isLoading = false,
  grouping,
  statusFilter,
  filters,
  onShipmentsChanged,
}: FirstLevelCardProps) {
  const toast = useToast();
  const [expandedSecondLevel, setExpandedSecondLevel] = useState<Set<string>>(
    new Set()
  );
  const [secondLevelEntities, setSecondLevelEntities] = useState<
    SecondLevelEntity[]
  >([]);
  const [secondLevelLoading, setSecondLevelLoading] = useState(false);

  // <--------------Directly fetchng shipments for logged in supplier----------------->
  const { clientId } = getGlobalApiConfig() ?? { clientId: undefined };
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const loggedInSupplier = secondLevelEntities?.[0];

  const fetchShipments = useCallback(async () => {
    if (!entity._id || !loggedInSupplier?._id) return;
    setShipmentsLoading(true);
    try {
      const response = await getShipments(
        entity._id,
        loggedInSupplier?._id,
        grouping,
        statusFilter,
        filters
      );
      setShipments(response);
    } catch (error) {
      console.error("Error fetching shipments:", error);
      toast({
        title: "Error",
        description: "Failed to fetch shipments for this entity",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setShipmentsLoading(false);
    }
  }, [
    entity._id,
    loggedInSupplier?._id,
    grouping,
    statusFilter,
    filters,
    toast,
  ]);

  useEffect(() => {
    setShipments([]);
    setSecondLevelEntities([]);
    setExpandedSecondLevel(new Set());
  }, [entity._id, grouping, statusFilter, filters]);

  useEffect(() => {
    if (
      expanded &&
      shipments.length === 0 &&
      clientId === ClientId.SUPPLIER_APP
    ) {
      fetchShipments();
    }
  }, [expanded, shipments.length, fetchShipments]);
  // <------------------------------------------------------------------------>

  const fetchSecondLevelEntities = useCallback(async () => {
    if (!entity._id || secondLevelEntities.length > 0) return;
    setSecondLevelLoading(true);
    try {
      const response = await getSecondLevelEntities(
        entity._id,
        grouping,
        statusFilter,
        filters
      );
      setSecondLevelEntities(response);
    } catch (error) {
      console.error("Error fetching second level entities:", error);
      toast({
        title: "Error",
        description: `Failed to fetch ${
          grouping === "customer"
            ? "suppliers"
            : grouping === "supplier"
            ? "customers"
            : "customers"
        } for this ${grouping}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSecondLevelLoading(false);
    }
  }, [
    entity._id,
    secondLevelEntities.length,
    grouping,
    statusFilter,
    filters,
    toast,
  ]);

  const refreshSecondLevelEntities = useCallback(async () => {
    if (!entity._id) return;
    setSecondLevelLoading(true);
    try {
      const response = await getSecondLevelEntities(
        entity._id,
        grouping,
        statusFilter,
        filters
      );
      setSecondLevelEntities(response);
    } catch (error) {
      console.error("Error refreshing second level entities:", error);
    } finally {
      setSecondLevelLoading(false);
    }
  }, [entity._id, grouping, statusFilter, filters]);

  const handleShipmentsChanged = useCallback(async () => {
    if (clientId === ClientId.SUPPLIER_APP) {
      await fetchShipments();
    } else {
      await refreshSecondLevelEntities();
    }
    await onShipmentsChanged?.();
  }, [
    clientId,
    fetchShipments,
    refreshSecondLevelEntities,
    onShipmentsChanged,
  ]);

  useEffect(() => {
    if (expanded && secondLevelEntities.length === 0) {
      fetchSecondLevelEntities();
    }
  }, [expanded, secondLevelEntities.length, fetchSecondLevelEntities]);

  const toggleSecondLevelExpansion = (entityId: string) => {
    setExpandedSecondLevel((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(entityId)) {
        newSet.delete(entityId);
      } else {
        newSet.add(entityId);
      }
      return newSet;
    });
  };

  const entityAddress = useMemo(() => {
    return buildAddress({
      firstLine: entity?.address?.addressLine1 || "",
      secondLine: entity?.address?.addressLine2 || "",
      city: entity?.address?.city || "",
      state: entity?.address?.state || "",
      pincode: "",
    });
  }, [entity]);

  const getEntityIcon = () => {
    if (grouping === "customer") return FaTruck;
    if (grouping === "supplier") return FaBox;
    return FaMotorcycle;
  };

  const getEntityTypeLabel = () => {
    if (grouping === "customer") return "Customer";
    if (grouping === "supplier") return "Supplier";
    return "Rider";
  };

  const getSecondLevelTypeLabel = () => {
    return grouping === "customer" ? "Supplier" : "Customer";
  };

  return (
    <Box
      bg="white"
      borderRadius="2xl"
      shadow="0 4px 20px rgba(0, 0, 0, 0.08)"
      border="1px"
      borderColor="gray.100"
      overflow="hidden"
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{
        shadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        transform: "translateY(-2px)",
      }}
      mb={3}
    >
      {/* Header Section */}
      <Box
        bg="linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)"
        p={6}
        position="relative"
        overflow="hidden"
        borderBottom="1px"
        borderColor="gray.100"
        onClick={onToggleExpansion}
        cursor={"pointer"}
      >
        {/* Subtle Background Pattern */}
        <Box
          position="absolute"
          top="-100px"
          right="-100px"
          w="300px"
          h="300px"
          opacity={0.03}
          bg="linear-gradient(135deg, #64748b 0%, #475569 100%)"
          borderRadius="full"
          transform="translate(50px, -50px)"
        />
        {/* Additional subtle pattern */}
        <Box
          position="absolute"
          bottom="-80px"
          left="-80px"
          w="200px"
          h="200px"
          opacity={0.02}
          bg="linear-gradient(135deg, #475569 0%, #334155 100%)"
          borderRadius="full"
          transform="translate(-50px, 50px)"
        />

        <VStack align="stretch" spacing={6} position="relative" zIndex={1}>
          {/* Main Header */}
          <HStack justify="space-between" align="flex-start">
            <VStack align="start" spacing={3} flex={1}>
              <VStack align="start" spacing={1.5}>
                <HStack spacing={3}>
                  <Heading
                    size="md"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="-0.025em"
                    lineHeight="1.2"
                  >
                    {entity.name}
                  </Heading>
                  {grouping === "rider" && entity.phone ? (
                    <Text fontSize="sm" color="gray.600" fontWeight="500">
                      {entity.phone}
                    </Text>
                  ) : null}
                  <HStack spacing={3}>
                    <Badge
                      bg="white"
                      color="gray.700"
                      variant="subtle"
                      px={3}
                      py={0.5}
                      borderRadius="full"
                      fontWeight="600"
                      border="1px"
                      borderColor="gray.200"
                      lineHeight={"normal"}
                      textTransform={"none"}
                    >
                      {entity.totalShipmentsCount} Shipments
                    </Badge>
                    {entity.engagementMode && (
                      <EngagementModeBadge
                        engagementMode={entity.engagementMode}
                        size="sm"
                      />
                    )}
                  </HStack>
                </HStack>
                {/* Address — riders have no pickup/drop address on the group card */}
                {grouping !== "rider" && entityAddress ? (
                  <HStack spacing={2} color="gray.600">
                    <Icon as={FaMapMarkerAlt} boxSize={3} color="blue.500" />
                    <GaraazTextComponent
                      header=""
                      content={entityAddress}
                      maxNoOfLines={3}
                      contentStyle={{
                        width: "400px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "gray.700",
                        cursor: "pointer",
                      }}
                    />
                  </HStack>
                ) : null}
              </VStack>
            </VStack>

            {/* Stats and Action Buttons */}
            <HStack spacing={4} align="center">
              {/* Stats - Integrated Design */}
              <HStack spacing={8} align="center">
                {statusFilter === "undelivered" ? (
                  <VStack spacing={1} align="center" width={"100px"}>
                    <HStack spacing={2} align="center">
                      <Icon as={FaTruck} color="blue.500" boxSize={3.5} />
                      <Text fontSize="sm" color="gray.600" fontWeight="600">
                        Undelivered
                      </Text>
                    </HStack>
                    <Text fontSize="xl" fontWeight="700" color="gray.800">
                      {entity?.totalUndeliveredShipments}
                    </Text>
                  </VStack>
                ) : statusFilter === "delivered" ? (
                  <VStack spacing={1} align="center" width={"100px"}>
                    <HStack spacing={2} align="center">
                      <Icon as={FcShipped} boxSize={4} />
                      <Text fontSize="sm" color="gray.600" fontWeight="600">
                        Delivered
                      </Text>
                    </HStack>
                    <Text fontSize="xl" fontWeight="700" color="gray.800">
                      {entity?.totalDeliveredShipments || 0}
                    </Text>
                  </VStack>
                ) : (
                  <VStack spacing={1} align="center" width={"100px"}>
                    <HStack spacing={2} align="center">
                      <Box position="relative">
                        <Icon as={FaTruck} color="red.500" boxSize={4} />
                        <Icon
                          as={FaTimes}
                          color="red.500"
                          boxSize={2}
                          position="absolute"
                          top="30%"
                          left="30%"
                          transform="translate(-50%, -50%)"
                          bg="white"
                        />
                      </Box>
                      <Text fontSize="sm" color="gray.600" fontWeight="600">
                        Cancelled
                      </Text>
                    </HStack>
                    <Text fontSize="xl" fontWeight="700" color="gray.800">
                      {entity?.cancelledShipmentsCount || 0}
                    </Text>
                  </VStack>
                )}
              </HStack>
              {/* Action Buttons */}
              <IconButton
                aria-label="Toggle expansion"
                icon={
                  expanded ? (
                    <ChevronUpIcon boxSize={10} />
                  ) : (
                    <ChevronDownIcon boxSize={10} />
                  )
                }
                variant="ghost"
                _hover={{
                  bg: "none",
                }}
              />
            </HStack>
          </HStack>

          {/* Loading State */}
          {isLoading && (
            <HStack
              spacing={3}
              justify="center"
              bg="white"
              p={4}
              borderRadius="xl"
              shadow="0 2px 8px rgba(0, 0, 0, 0.06)"
            >
              <Spinner size="sm" color="blue.500" />
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                Loading {getEntityTypeLabel().toLowerCase()}...
              </Text>
            </HStack>
          )}
        </VStack>
      </Box>
      {ClientId.SUPPLIER_APP !== clientId ? (
        <Collapse in={expanded}>
          <Box p={6} bg="gray.50">
            {secondLevelLoading ? (
              <Center py={8}>
                <VStack spacing={4}>
                  <Spinner size="lg" color="blue.500" />
                  <Text color="gray.600" fontSize="sm">
                    Loading {getSecondLevelTypeLabel().toLowerCase()}s for{" "}
                    {entity.name}...
                  </Text>
                </VStack>
              </Center>
            ) : secondLevelEntities.length === 0 ? (
              <Center py={8}>
                <VStack spacing={3}>
                  <Box p={4} bg="gray.100" borderRadius="full">
                    <Icon as={getEntityIcon()} boxSize={6} color="gray.400" />
                  </Box>
                  <Text color="gray.500" fontSize="sm">
                    No {getSecondLevelTypeLabel().toLowerCase()}s found for this{" "}
                    {getEntityTypeLabel().toLowerCase()}
                  </Text>
                </VStack>
              </Center>
            ) : (
              <VStack spacing={4} align="stretch">
                {secondLevelEntities.map((secondLevelEntity) => (
                  <SecondLevelCard
                    key={secondLevelEntity._id}
                    entity={secondLevelEntity}
                    expanded={expandedSecondLevel.has(secondLevelEntity._id)}
                    onToggleExpansion={() =>
                      toggleSecondLevelExpansion(secondLevelEntity._id)
                    }
                    isLoading={false}
                    firstLevelId={entity._id}
                    grouping={grouping}
                    shipmentStatusFilter={statusFilter}
                    filters={filters}
                    onShipmentsChanged={handleShipmentsChanged}
                  />
                ))}
              </VStack>
            )}
          </Box>
        </Collapse>
      ) : (
        <Collapse in={expanded}>
          <Box p={0}>
            {shipmentsLoading ? (
              <Center py={8}>
                <VStack spacing={3}>
                  <Spinner size="md" color="blue.500" />
                  <Text color="gray.600" fontSize="sm">
                    Loading shipments for {entity.name}...
                  </Text>
                </VStack>
              </Center>
            ) : shipments.length === 0 ? (
              <Center py={8}>
                <VStack spacing={2}>
                  <Box p={3} bg="gray.100" borderRadius="full">
                    <Icon as={FaTruck} boxSize={4} color="gray.400" />
                  </Box>
                  <Text color="gray.500" fontSize="sm">
                    No shipments found for this{" "}
                    {getEntityTypeLabel().toLowerCase()}
                  </Text>
                </VStack>
              </Center>
            ) : (
              <Box overflow="hidden" borderRadius="lg">
                <Box
                  maxH="72"
                  overflowY="auto"
                  overflowX="hidden"
                  sx={{
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#c1c1c1",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#a8a8a8",
                    },
                  }}
                >
                  <ShipmentsTable
                    shipments={shipments}
                    onShipmentsChanged={handleShipmentsChanged}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
